import { useEffect, useMemo, useState, type MouseEvent, type ReactNode } from 'react';
import {
  generateStateKey,
  hbIf,
  hbStr,
  ifCompare,
  readGeneratedReport,
  subscribeGeneratedReports,
  type CachedGeneratedReport,
} from '../reportTabData';
import type { GeneratedTabPaneProps } from './GeneratedReportTabs';

/**
 * Phase 5d — the "PHX Selection" tab (tabs/phx_selection/view/
 * phx_selection.js + tmpl/phx_selection.tmpl + tmpl/report.ahu.tmpl). This is
 * the one 5d tab with a LIVE POST→socket→in-tab-render leg:
 *
 *  - 'generate:report:phx_selection' (phx_selection.js:176-216): POST
 *    `phx_selection` with the whole state; on POST success render() the
 *    static shell (:186-188 — hence `rendered` gates the shell here, like
 *    the 5c tabs); a LIVE (uncommented) `Promise.all([fn['create.websocket'],
 *    req_task])` socket wait (:196-211) matches the celery result by task_id,
 *    stashes it under the state key and calls renderReport. The kit's
 *    phx_selection dispatch performs the POST + socket wait and writes the
 *    result into the reportTabData cache; this pane subscribes to it.
 *  - renderReport (:54-95): maps data.phx_selection_data (adding a
 *    casing_length_all no template consumes, :59-62), renders report.ahu.tmpl
 *    into `.content-item` — and then CRASHES: validateOutputs (:372-391)
 *    references an undeclared `number` variable (:387) and throws a
 *    ReferenceError on the first `.number_output` element with non-empty
 *    text() (inputs return '' from .text() and are skipped; the Properties
 *    output tab always contains spans with whitespace-padded interpolations,
 *    e.g. plate_spacing tmpl:1132-1134, so the throw is unconditional in
 *    practice). EVERYTHING after the html() insert is therefore dead on the
 *    live board: setTimeStamp (:73) — the header stays "Generated on: " —
 *    the sticky-heading scroll binding (:78-82, no .table-wrapper exists in
 *    the template anyway) and the bindFormatter loop (:85-92) — every value
 *    renders RAW, data-formatter_type attributes notwithstanding. This port
 *    reproduces the OBSERVABLE result (raw values, empty timestamp) without
 *    re-throwing the legacy console error.
 *  - 'open:generated_report:phx_selection' (:166-174) is LIVE: re-opening
 *    the tab re-renders from the cache (REOPENABLE_TABS + the page's reopen
 *    flow), with the same crash semantics.
 *
 *  Dead legacy code, deliberately NOT ported:
 *  - export_csv (:236-295): its only trigger chain starts at the header's
 *    'click #export_csv' (main_header.js:62) and tmpl/main_header.tmpl no
 *    longer renders that button — unreachable.
 *  - print (:297-328): triggered via Ctrl+P → main_header.js print()
 *    (:128-132, :861-862). The page-level Ctrl+P print harness is 5e scope
 *    (see the ReportsBoardPage note); it would also crash in validateOutputs
 *    (:323) before window.print().
 *  - events 'click .phx_counts_numbers' → changePhx (:25): the method does
 *    not exist on the view; '#notes' → showNotes targets a
 *    '.phx_selector_notes .popup-wrapper' that no template renders.
 *
 *  Multi-AHU quirk preserved: phx fans out into N single-AHU dispatches; the
 *  legacy socket handler timestamps `self.data[self.current_state]` (the
 *  LAST dispatch) while storing under its own closure key (:205-207), so a
 *  result for an earlier AHU throws before renderReport — only the LAST
 *  AHU's table ever renders (all results still land in the cache for
 *  re-open). This pane renders the last dispatch state's cache entry only.
 */

type Row = Record<string, unknown>;

const sub = (m: Row, key: string): Row => ((m[key] ?? {}) as Row);
const stop = (e: MouseEvent) => e.preventDefault();

/** Readonly input — phx values are NEVER formatter-bound (the crash), so the
 *  value is always the raw interpolation. `cls` present (even '') means the
 *  legacy input also carried type="text". */
const ROInput = ({
  value,
  cls,
  fmt,
}: {
  value: unknown;
  cls?: string;
  fmt?: 'integer' | 'decimal';
}) => (
  <input
    readOnly
    tabIndex={-1}
    value={hbStr(value)}
    {...(cls !== undefined || fmt !== undefined ? { type: 'text' } : {})}
    className={cls}
    data-formatter_type={fmt}
  />
);

function extractModels(entry: CachedGeneratedReport | undefined): Row[] | null {
  const data = entry?.message?.data as { phx_selection_data?: Row[] } | undefined;
  if (!data || !Array.isArray(data.phx_selection_data)) return null;
  return data.phx_selection_data;
}

const PhxSelectionTab = ({ report, rendered, mode, states, reopenKey }: GeneratedTabPaneProps) => {
  // The cache key this pane renders: reopen key, or the LAST dispatch state
  // (the legacy self.current_state, phx_selection.js:181).
  const key = useMemo(() => {
    if (mode === 'reopen') return reopenKey ?? null;
    const last = states && states.length ? states[states.length - 1] : null;
    return last ? generateStateKey(last) : null;
  }, [mode, states, reopenKey]);

  const [models, setModels] = useState<Row[] | null>(() =>
    key ? extractModels(readGeneratedReport(key)) : null
  );

  // Wait for the socket result (generate mode) / key changes.
  useEffect(() => {
    if (!key) return undefined;
    setModels(extractModels(readGeneratedReport(key)));
    return subscribeGeneratedReports(() => {
      setModels(extractModels(readGeneratedReport(key)));
    });
  }, [key]);

  // Shared tab state — legacy summerWinterTab toggles globally (:114-139).
  const [season, setSeason] = useState<'winter' | 'summer'>('winter');
  // Per-model bootstrap tab groups (input menu / properties + bypass
  // sub-menus / output menu), keyed by model index.
  const [inputTabs, setInputTabs] = useState<Record<number, string>>({});
  const [moreTabs, setMoreTabs] = useState<Record<number, string>>({});
  const [bypassTabs, setBypassTabs] = useState<Record<number, string>>({});
  const [outputTabs, setOutputTabs] = useState<Record<number, string>>({});

  // The legacy pane is EMPTY until the generate POST succeeds
  // (success → render(), phx_selection.js:186-188). Reopen renders directly.
  const showShell = mode === 'reopen' || rendered;

  return (
    <div id={report.value} className="col-xs-12 active" style={{ display: 'block' }}>
      {showShell && (
        <>
          {/* phx_selection.tmpl:1-14 — the time-stamp NEVER gets filled: the
              validateOutputs ReferenceError aborts renderReport before
              setTimeStamp (:72-73). */}
          <div className="container-header clearfix">
            <h3 className="left">
              <span className="group-name" tooltip={`: ${report.name}`}>
                Engineering
              </span>
              <span className="report-name">{report.name}</span>
              <span className="time-stamp">Generated on: </span>
            </h3>
          </div>
          <div className="col-xs-12 phx-data-content">
            <div className="content">
              <div className="content-item content-with-page-navbar">
                {models ? (
                  models.map((m, idx) => (
                    <PhxModel
                      key={idx}
                      m={m}
                      idx={idx}
                      season={season}
                      setSeason={setSeason}
                      inputTab={inputTabs[idx] ?? 'Properties'}
                      setInputTab={(t) => setInputTabs((prev) => ({ ...prev, [idx]: t }))}
                      moreTab={moreTabs[idx] ?? 'properties_more'}
                      setMoreTab={(t) => setMoreTabs((prev) => ({ ...prev, [idx]: t }))}
                      bypassTab={bypassTabs[idx] ?? 'general_more'}
                      setBypassTab={(t) => setBypassTabs((prev) => ({ ...prev, [idx]: t }))}
                      outputTab={outputTabs[idx] ?? 'ahu-Properties_output'}
                      outputClicked={outputTabs[idx] !== undefined}
                      setOutputTab={(t) => setOutputTabs((prev) => ({ ...prev, [idx]: t }))}
                    />
                  ))
                ) : (
                  <div className="preloader dotted-preloader active" />
                )}
              </div>
            </div>
          </div>
          <div className="phx-data-print" />
        </>
      )}
    </div>
  );
};

interface PhxModelProps {
  m: Row;
  idx: number;
  season: 'winter' | 'summer';
  setSeason: (s: 'winter' | 'summer') => void;
  inputTab: string;
  setInputTab: (t: string) => void;
  moreTab: string;
  setMoreTab: (t: string) => void;
  bypassTab: string;
  setBypassTab: (t: string) => void;
  outputTab: string;
  outputClicked: boolean;
  setOutputTab: (t: string) => void;
}

/** A label/value input row used across the phx input tabs. */
const InRow = ({
  label,
  labelAttrs,
  children,
}: {
  label: ReactNode;
  labelAttrs?: Record<string, string>;
  children: ReactNode;
}) => (
  <div className="row">
    <div className="col-xs-6 label-part" {...labelAttrs}>
      {label}
    </div>
    <div className="col-xs-6 input-part">
      <div className="row">{children}</div>
    </div>
  </div>
);

const Col12 = ({ children }: { children: ReactNode }) => (
  <div className="col-xs-12">{children}</div>
);

/** Output label/value row (output-part panes). */
const OutRow = ({
  label,
  uom,
  children,
  cls = 'row row_with_negative_margin',
  labelCols = 'col-xs-6',
  valueCols = 'col-xs-6',
}: {
  label: ReactNode;
  uom?: ReactNode;
  children: ReactNode;
  cls?: string;
  labelCols?: string;
  valueCols?: string;
}) => (
  <div className={cls}>
    <div className={`${labelCols} label-part`}>
      {label}
      {uom}
    </div>
    <div className={`${valueCols} output-part`}>{children}</div>
  </div>
);

/** One `{{#each models}}` block of tabs/phx_selection/tmpl/report.ahu.tmpl. */
const PhxModel = ({
  m,
  idx,
  season,
  setSeason,
  inputTab,
  setInputTab,
  moreTab,
  setMoreTab,
  bypassTab,
  setBypassTab,
  outputTab,
  outputClicked,
  setOutputTab,
}: PhxModelProps) => {
  const i = String(idx); // {{@index}}
  const mp = sub(m, 'master_phx');
  const idCls = `plate_heat_exchanger_${hbStr(m.id)}`;
  const bypassNonClickable = ifCompare(sub(m, 'model').configuration_id, '==', 1); // tmpl:14 — `model` is the model-name scalar → never true on live
  const winterDisabled = ifCompare(m.oa_airflow_actual_winter, '<=', 0);
  const summerDisabled = ifCompare(m.oa_airflow_actual_summer, '<=', 0);
  const warnNotes = hbIf((m as Row).splitedNotes) || ifCompare(m.ok, '==', 'false'); // tmpl:1065

  const inputLi = (name: string, label: string, liExtra?: string, aClass?: string) => {
    const sel = inputTab === name;
    return (
      <li className={`${sel ? 'selected ' : ''}${i}${name} input_label${liExtra ?? ''}${sel ? ' active' : ''}`}>
        <a
          tabIndex={-1}
          className={aClass}
          data-toggle="tab"
          href={`#${i}${name}`}
          onClick={(e) => {
            stop(e);
            setInputTab(name);
          }}
        >
          {label}
        </a>
      </li>
    );
  };
  // Hidden .tab-pane elements are display:none whether or not the template's
  // initial markup carried a stray 'in' (bootstrap strips it on the first
  // switch anyway) — active panes get 'in active'.
  const paneCls = (name: string, extra: string) =>
    `tab-pane fade${inputTab === name ? ' in active' : ''} ${extra}`;

  const subLi = (
    current: string,
    set: (t: string) => void,
    name: string,
    tabCls: string,
    label: ReactNode,
    dataTab?: string
  ) => (
    <li className={`${name}${current === name ? ' active' : ''} ${tabCls}${name === 'general_more' && current === name ? ' selected' : ''}`}>
      <a
        tabIndex={-1}
        data-toggle="tab"
        data-tab={dataTab}
        href={`#${name}`}
        onClick={(e) => {
          stop(e);
          set(name);
        }}
      >
        {label}
      </a>
    </li>
  );

  const subPane = (current: string, name: string, children: ReactNode) => (
    <div className={`tab-pane fade${current === name ? ' in active' : ''} scrollable-content col-xs-12 float-left`} id={name}>
      {children}
    </div>
  );

  return (
    <div className={`${idCls} report_plate_heat_exchanger_new report-per-page col-xs-12`} style={{ height: 'calc(100vh - 216px)', marginBottom: 20 }}>
      <div id="plate_heat_exchanger_new" className={`${idCls} plate_heat_exchanger_new report-per-page report_plate_heat_exchanger_new plate_heat_exchanger-rating-mode ${i} input-part input-parts rating row`}>
        <div className="col-xs-1 nav nav-tabs">
          <div className="row">
            <ul className="col-xs-12 input-menu">
              <li>Input</li>
              {inputLi('Project', 'Project')}
              {inputLi('Properties', 'Properties')}
              {inputLi(
                'Bypass',
                'Bypass',
                ` bypassLabel${bypassNonClickable ? ' non-clickable ' : ''}`,
                bypassNonClickable ? ' non-clickable ' : undefined
              )}
              {inputLi('Performance', 'Performance')}
              {inputLi('Settings', 'Settings')}
              {inputLi('Pricing', 'Pricing')}
            </ul>
          </div>
        </div>
        <div className="col-xs-11 content-data tab-content">
          <div className="row">
            {/* ---- Input · Project (tmpl:32-89) ---- */}
            <div className={paneCls('Project', 'scrollable-content col-xs-12 float-left')} id={`${i}Project`}>
              <div className="col-xs-12 col-md-12 col-lg-4">
                <InRow label="Project Name">
                  <Col12>
                    <ROInput value={m.project_name} cls="" />
                  </Col12>
                </InRow>
                <InRow label="AHU">
                  <Col12>
                    <ROInput value={m.ahu_tag} cls="" />
                  </Col12>
                </InRow>
                <InRow label="Section">
                  <Col12>
                    <ROInput value={m.section_tag} cls="" />
                  </Col12>
                </InRow>
              </div>
              <div className="col-xs-12 col-md-12 col-lg-6 col-xs-offset-2  project-group">
                <div className="row">
                  <div className="col-xs-5 label-part">
                    <span data-name="elevation">Altitude</span>
                    <span className="percent right">ft </span>
                  </div>
                  <div className="col-xs-7 input-part">
                    <div className="row">
                      <Col12>
                        <input
                          className="search_field required_field project_field has_default"
                          autoComplete="off"
                          type="text"
                          data-formatter_type="integer"
                          readOnly
                          tabIndex={-1}
                          data-min="0"
                          data-step="1"
                          data-max="15000"
                          name="elevation"
                          data-name="altitude"
                          value={hbIf(m.elevation) ? hbStr(m.elevation) : '0'}
                        />
                      </Col12>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ---- Input · Properties (tmpl:90-466) ---- */}
            <div className={paneCls('Properties', 'scrollable-content col-xs-12 float-left')} id={`${i}Properties`}>
              <div className="col-xs-12 sub-tab-menu nav nav-tabs">
                <div className="row">
                  <Col12>
                    <ul className="col-xs-12 text-right more_tabing">
                      {subLi(moreTab, setMoreTab, 'properties_more', 'properties-tab', <>General&#160;</>)}
                      {subLi(moreTab, setMoreTab, 'internal_plates_more', ' internal_plates-tab', <>Internal Plates&#160;</>)}
                      {subLi(moreTab, setMoreTab, 'end_plates_more', 'end_plates-tab', <> End Plates&#160;</>, 'end_plates')}
                      {subLi(moreTab, setMoreTab, 'corner_profiles_more', 'corner_profiles-tab', <> Corner Profiles</>, 'corner_profiles')}
                    </ul>
                  </Col12>
                </div>
              </div>
              <div className="more-tab-content">
                {subPane(
                  moreTab,
                  'properties_more',
                  <div className="row">
                    <div className="col-xs-12 col-md-12 col-lg-4 input-part-left">
                      <InRow label="Manufacturer" labelAttrs={{ 'data-name': 'mfg_id' }}>
                        <Col12>
                          <ROInput value={m.phx_mfg} />
                        </Col12>
                      </InRow>
                      <InRow label={<span data-name="series_id">Series</span>}>
                        <Col12>
                          <ROInput value={mp.series} />
                        </Col12>
                      </InRow>
                      <InRow label="Size" labelAttrs={{ 'data-name': 'size_id' }}>
                        <Col12>
                          <ROInput value={mp.size} />
                        </Col12>
                      </InRow>
                      <InRow label="Flow Type" labelAttrs={{ 'data-name': 'flow_type_id' }}>
                        <Col12>
                          <ROInput value={mp.flow_type} />
                        </Col12>
                      </InRow>
                      <InRow
                        label={
                          <>
                            Temperature Rating
                            <span className="right temp uom">F</span>
                          </>
                        }
                        labelAttrs={{ 'data-name': 'temp_rating_id' }}
                      >
                        <Col12>
                          <ROInput value={m.temp_rating} />
                        </Col12>
                      </InRow>
                      <InRow label="Seal" labelAttrs={{ 'data-name': 'seal_id' }}>
                        <Col12>
                          <ROInput value={m.seal} />
                        </Col12>
                      </InRow>
                    </div>
                    <div className="col-xs-12 col-md-12 col-lg-6  col-xs-offset-2 input-part-right">
                      <InRow
                        label={
                          <>
                            Casing Width* /<span className="label-part" data-name="casing_width_max"> Max</span>
                            <span className="right uom">in</span>
                          </>
                        }
                        labelAttrs={{ 'data-name': 'casing_width' }}
                      >
                        <div className="col-xs-6 qty-fl-select">
                          <ROInput value={m.casing_width} />
                        </div>
                        <div className="col-xs-6 qty-fl-select">
                          <ROInput value={m.casing_width_max} />
                        </div>
                      </InRow>
                      <InRow
                        label={
                          <>
                            Casing Height* /<span className="label-part" data-name="casing_height_max"> Max</span>
                            <span className="right uom">in</span>
                          </>
                        }
                        labelAttrs={{ 'data-name': 'casing_height' }}
                      >
                        <div className="col-xs-6">
                          <ROInput value={mp.casing_height} />
                        </div>
                        <div className="col-xs-6">
                          <ROInput value={mp.casing_height_max} />
                        </div>
                      </InRow>
                      <InRow label={<span data-name="mounting_id">Mounting</span>}>
                        <Col12>
                          <ROInput value={m.mounting} />
                        </Col12>
                      </InRow>
                      <InRow label={<span data-name="configuration_id">Configuration</span>}>
                        <Col12>
                          <ROInput value={m.configuration} />
                        </Col12>
                      </InRow>
                      <div className="row bank_wide_bank_high">
                        <div className="col-xs-6 label-part">
                          <span data-name="bank_wide">Bank Wide </span> x <span data-name="bank_high">High</span>
                        </div>
                        <div className="col-xs-6 input-part">
                          <div className="row">
                            <div className="col-xs-5 ">
                              <ROInput value={m.bank_wide} />
                            </div>
                            <div className="col-xs-2 padding-top-9">
                              <span className="unit not-uom ">x</span>
                            </div>
                            <div className="col-xs-5">
                              <ROInput value={m.bank_high} />
                            </div>
                          </div>
                        </div>
                      </div>
                      <InRow label={<span data-name="face_one_definition_id" data-name_2="face_two_definition_id">Face Definition 1/2</span>}>
                        <div className="col-xs-6 ">
                          <ROInput value={m.face_one_definition} />
                        </div>
                        <div className="col-xs-6">
                          <ROInput value={m.face_two_definition} />
                        </div>
                      </InRow>
                    </div>
                  </div>
                )}
                {subPane(
                  moreTab,
                  'internal_plates_more',
                  <div className="row">
                    <div className="col-xs-12 col-md-12 col-lg-4 input-part-left">
                      <div className="row">
                        <div className="col-xs-6 label-part">
                          <span data-name="" className="text-uppercase sub-titles__text">Internal Plates</span>
                        </div>
                      </div>
                      <InRow label={<span data-name="plate_spacing_code_id">Spacing</span>}>
                        <Col12>
                          <ROInput value={mp.plate_spacing_code} />
                        </Col12>
                      </InRow>
                      <InRow label={<span data-name="plate_mtl_id">Material</span>}>
                        <Col12>
                          <ROInput value={m.plate_mtl} />
                        </Col12>
                      </InRow>
                      <InRow label={<span data-name="plate_pattern_id">Pattern</span>}>
                        <Col12>
                          <ROInput value={m.plate_pattern} />
                        </Col12>
                      </InRow>
                      <InRow label={<span data-name="plate_finish_id">Finish</span>}>
                        <Col12>
                          <ROInput value={m.plate_finish} />
                        </Col12>
                      </InRow>
                      <InRow label={<span data-name="plate_orientation_id">Orientation</span>}>
                        <Col12>
                          <ROInput value={m.plate_orientation} />
                        </Col12>
                      </InRow>
                    </div>
                  </div>
                )}
                {subPane(
                  moreTab,
                  'end_plates_more',
                  <div className="row">
                    <div className="col-xs-12 col-md-12 col-lg-4 input-part-left">
                      <div className="row">
                        <div className="col-xs-6 label-part">
                          <span data-name="" className="text-uppercase sub-titles__text">End Plates</span>
                        </div>
                      </div>
                      <InRow label={<span data-name="end_plate_style_id">Style</span>}>
                        <Col12>
                          <ROInput value={m.end_plate_style} />
                        </Col12>
                      </InRow>
                      <InRow
                        label={
                          <>
                            <span data-name="end_plate_flange">Flange</span>
                            <span className="right uom">in</span>
                          </>
                        }
                      >
                        <Col12>
                          <ROInput value={m.end_plate_flange} />
                        </Col12>
                      </InRow>
                      <InRow label={<span data-name="end_plate_mtl_id">Material</span>}>
                        <Col12>
                          <ROInput value={m.end_plate_mtl} />
                        </Col12>
                      </InRow>
                      <InRow label={<span data-name="end_plate_finish_id">Finish</span>}>
                        <Col12>
                          <ROInput value={m.end_plate_finish} />
                        </Col12>
                      </InRow>
                    </div>
                  </div>
                )}
                {subPane(
                  moreTab,
                  'corner_profiles_more',
                  <div className="row">
                    <div className="col-xs-12 col-md-12 col-lg-4 input-part-left">
                      <div className="row">
                        <div className="col-xs-6 label-part">
                          <span data-name="" className="text-uppercase sub-titles__text">Corner Profiles</span>
                        </div>
                      </div>
                      <InRow label={<span data-name="corner_profile_style_id">Style</span>}>
                        <Col12>
                          <ROInput value={m.corner_profile_style} />
                        </Col12>
                      </InRow>
                      <InRow label={<span data-name="corner_profile_mtl_id">Material</span>}>
                        <Col12>
                          <ROInput value={m.corner_profile_mtl} />
                        </Col12>
                      </InRow>
                      <InRow label={<span data-name="corner_profile_finish_id">Finish</span>}>
                        <Col12>
                          <ROInput value={m.corner_profile_finish} />
                        </Col12>
                      </InRow>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* ---- Input · Bypass (tmpl:467-669) ---- */}
            <div className={paneCls('Bypass', ' scrollable-content col-xs-12 float-left')} id={`${i}Bypass`}>
              <div className="col-xs-12 sub-tab-menu nav nav-tabs">
                <div className="row">
                  <ul className="col-xs-12 text-right more_tabing">
                    {subLi(bypassTab, setBypassTab, 'general_more', 'general-tab', <>General &#160;</>)}
                    {subLi(bypassTab, setBypassTab, 'bypass_more', ' bypass-tab', <>Bypass Damper &#160;</>)}
                    {subLi(bypassTab, setBypassTab, 'recirculation_more', ' recirculation-tab', <>Recirculation</>)}
                  </ul>
                </div>
              </div>
              <div className="more-tab-content">
                {subPane(
                  bypassTab,
                  'general_more',
                  <div className="row">
                    <div className="col-xs-12 col-md-12 col-lg-4 input-part-left">
                      <div className="row">
                        <div className="col-xs-6 label-part  sub-titles">
                          <span data-name="" className="text-uppercase sub-titles__text">General</span>
                        </div>
                      </div>
                      <InRow label="Type" labelAttrs={{ 'data-name': 'bypass_type_id' }}>
                        <Col12>
                          <ROInput value={m.bypass_type} />
                        </Col12>
                      </InRow>
                      <InRow label="Location" labelAttrs={{ 'data-name': 'bypass_location_id' }}>
                        <Col12>
                          <ROInput value={m.bypass_location} />
                        </Col12>
                      </InRow>
                      <InRow
                        label={
                          <>
                            <span data-name="bypass_width">Width</span>
                            <span className="right uom">in</span>
                          </>
                        }
                      >
                        <Col12>
                          <ROInput value={m.bypass_width} cls="number_output" fmt="decimal" />
                        </Col12>
                      </InRow>
                    </div>
                  </div>
                )}
                {subPane(
                  bypassTab,
                  'bypass_more',
                  <div className="row">
                    <div className="col-xs-12 col-md-12 col-lg-4 input-part-left">
                      <div className="row">
                        <div className="col-xs-6 label-part  sub-titles">
                          <span data-name="" className="text-uppercase sub-titles__text">Bypass Damper</span>
                        </div>
                      </div>
                      {(
                        [
                          ['bypass_side_id', 'Side', m.bypass_side],
                          ['bypass_dmr_config_id', 'Configuration', m.bypass_dmr_config],
                          ['bypass_dmr_location_id', 'Damper Location', m.bypass_dmr_location],
                          ['bypass_dmr_drive_location_id', 'Drive Location', m.bypass_dmr_drive_location],
                        ] as Array<[string, string, unknown]>
                      ).map(([name, label, value]) => (
                        <div className="row bypass_type_open" key={name}>
                          <div className="col-xs-6 label-part" data-name={name}>
                            {label}
                          </div>
                          <div className="col-xs-6 input-part">
                            <div className="row">
                              <Col12>
                                <ROInput value={value} />
                              </Col12>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="col-xs-12 col-md-12 col-lg-6 col-xs-offset-2 input-part-right">
                      <div className="row empty" />
                      <div className="row bypass_type_open">
                        <div className="col-xs-6 label-part" data-name="bypass_dmr_mtl_id">
                          Material
                        </div>
                        <div className="col-xs-6 input-part">
                          <div className="row">
                            <Col12>
                              <ROInput value={m.bypass_dmr_mtl} />
                            </Col12>
                          </div>
                        </div>
                      </div>
                      <div className="row bypass_type_open">
                        <div className="col-xs-6 label-part" data-name="bypass_dmr_fin_id">
                          Finish
                        </div>
                        <div className="col-xs-6 input-part">
                          <div className="row">
                            <Col12>
                              <ROInput value={m.bypass_dmr_fin} />
                            </Col12>
                          </div>
                        </div>
                      </div>
                      <div className="row empty" />
                      <div className="row bypass_type_open">
                        <div className="col-xs-6 label-part">
                          <span data-name="dumper_supplied_by_id">Dampers Supplied By</span>
                        </div>
                        <div className="col-xs-6 input-part">
                          <div className="row">
                            <Col12>
                              <ROInput value={m.dmrs_supplied_by} />
                            </Col12>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {subPane(
                  bypassTab,
                  'recirculation_more',
                  <div className="row">
                    <div className="col-xs-12 col-md-12 col-lg-4 input-part-left">
                      <div className="row">
                        <div className="col-xs-6 label-part">
                          <span data-name="" className="text-uppercase sub-titles__text">Recirculation</span>
                        </div>
                      </div>
                      <InRow label="Configuration" labelAttrs={{ 'data-name': 'recirc_dmr_config_id' }}>
                        <Col12>
                          <ROInput value={m.recirc_dmr_config} />
                        </Col12>
                      </InRow>
                      <InRow label="Location" labelAttrs={{ 'data-name': 'recirc_dmr_location_id' }}>
                        <Col12>
                          <ROInput value={m.recirc_dmr_location} />
                        </Col12>
                      </InRow>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* ---- Input · Performance (tmpl:671-934) ---- */}
            <div className={paneCls('Performance', 'scrollable-content col-xs-12 float-left Performance')} id={`${i}Performance`}>
              <div className="col-xs-12 sub-tab-menu nav nav-tabs">
                <div className="row">
                  <ul className="col-xs-12 text-right performance_tabing summer-winter-tabing">
                    <li className={`winter winter-tab  selected${season === 'winter' ? ' active' : ''}${winterDisabled ? ' non-clickable' : ''}`}>
                      <a tabIndex={-1} data-toggle="tab" data-tab="winter" href="#winter" onClick={(e) => { stop(e); setSeason('winter'); }}>
                        winter &#160;
                      </a>
                    </li>
                    <li className={`summer summer-tab${season === 'summer' ? ' active' : ''}${summerDisabled ? ' non-clickable' : ''}`}>
                      <a tabIndex={-1} data-toggle="tab" data-tab="summer" href="#summer" onClick={(e) => { stop(e); setSeason('summer'); }}>
                        summer
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="performance-tab-content">
                <div id="winter" className={`float-left winter tab-pane fade${season === 'winter' ? ' in active' : ''}`}>
                  <PhxSeasonInput m={m} season="winter" />
                </div>
                <div id="summer" className={`float-left summer tab-pane fade${season === 'summer' ? ' in active' : ''}`}>
                  <PhxSeasonInput m={m} season="summer" />
                </div>
              </div>
            </div>

            {/* ---- Input · Settings (tmpl:935-1029) ---- */}
            <div className={paneCls('Settings', 'scrollable-content float-left col-xs-12')} id={`${i}Settings`}>
              <div className="col-xs-12 col-md-12 col-lg-4 input-part-left">
                <InRow label={<span data-name="type_desired_efficiency_id">Desired Efficiency Type</span>}>
                  <Col12>
                    <ROInput value={m.type_desired_efficiency} />
                  </Col12>
                </InRow>
                <InRow
                  label={
                    <>
                      <span data-name="min_desired_efficiency">Minimum Efficiency</span>
                      <span className="uom right">%</span>
                    </>
                  }
                >
                  <Col12>
                    <ROInput value={m.min_desired_efficiency} cls="number_output" fmt="decimal" />
                  </Col12>
                </InRow>
                <InRow
                  label={
                    <>
                      Max Allowed PD
                      <span className="right uom">in WC</span>
                    </>
                  }
                  labelAttrs={{ 'data-name': 'max_allowed_dp' }}
                >
                  <Col12>
                    <ROInput value={mp.max_allowed_dp} cls="number_output" fmt="decimal" />
                  </Col12>
                </InRow>
              </div>
              <div className="col-xs-12 col-md-12 col-lg-6 col-xs-offset-2 input-part-right">
                <InRow label={<span data-name="certifications">Certification</span>}>
                  <Col12>
                    {(Array.isArray(m.certification) ? (m.certification as Row[]) : []).map((c, ci) => (
                      <ROInput key={ci} value={c.name} />
                    ))}
                  </Col12>
                </InRow>
              </div>
            </div>

            {/* ---- Input · Pricing (tmpl:1030-1046) ---- */}
            <div className={paneCls('Pricing', 'scrollable-content col-xs-12')} id={`${i}Pricing`}>
              <div className="col-xs-12 col-md-12 col-lg-4 float-left">
                <InRow label={<span data-name="pricing_method_id">Method</span>}>
                  <div className="col-xs-12 right text-right">
                    <ROInput value={m.pricing_method} />
                  </div>
                </InRow>
                <div className="row pricingInputs" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ---- Output part (tmpl:1050-1775) ---- */}
      <div className={`${idCls} plate_heat_exchanger_new report-per-page report_plate_heat_exchanger_new plate_heat_exchanger-rating-mode ${i} output-part output-parts rating row`}>
        <div className="col-xs-1 nav nav-tabs">
          <div className="row">
            <ul className="col-xs-12 ouptut_tubs ouptut-menu">
              <li>Output</li>
              {(
                [
                  ['ahu-Properties_output', 'Properties', ''],
                  [
                    'ahu-Bypass_output',
                    'Bypass',
                    ifCompare(m.configuration, '!=', 'Heat-Exchanger w/Bypass') ? ' non-clickable ' : '',
                  ],
                  ['ahu-Performance_output', 'Performance', ' '],
                  ['ahu-Notes-output', 'Notes', ''],
                ] as Array<[string, string, string]>
              ).map(([name, label, extra]) => (
                <li
                  key={name}
                  data-tab={name}
                  className={`ahu-${label}-output output_label${extra}${
                    outputTab === name ? `${outputClicked ? ' selected' : ''} active` : ''
                  }`}
                >
                  <a
                    className={label === 'Notes' && warnNotes ? 'warning-notes' : undefined}
                    data-toggle="tab"
                    href={`#${name}`}
                    onClick={(e) => {
                      stop(e);
                      setOutputTab(name);
                    }}
                  >
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="col-xs-11 with-border tab-content">
          <div className="row tab-content-row row_with_negative_margin">
            <div className="full-height">
              {/* Output · Properties (tmpl:1073-1156) */}
              <div className={`tab-pane fade${outputTab === 'ahu-Properties_output' ? ' in active' : ''}  scrollable-content col-xs-12 float-left full-height`} id="ahu-Properties_output">
                <div className="row empty" />
                <div className="col-xs-12 col-md-12 col-lg-12 output-part-left">
                  <div className="row row_with_negative_margin">
                    <div className="col-xs-2 label-part ">
                      <span data-name="bypass_width_id">Model</span>
                    </div>
                    <div className="col-xs-9 output-part">
                      <span data-name="bypass_width_id">{hbStr(m.model)}</span>
                    </div>
                  </div>
                </div>
                <div className="col-xs-12 col-md-12 col-lg-4 output-part-left">
                  <div className="row empty" />
                  <OutRow label={<span data-name="bypass_width_id">Casing W x H x D</span>} uom={<span className="right uom">in</span>}>
                    <span data-name="bypass_width_id" className="number_output" data-format="decimal">{hbStr(m.casing_width)}</span>
                    <span className="unit not-uom "> x </span>
                    <span data-name="bypass_width_id" className="number_output" data-format="decimal">{hbStr(mp.casing_height)}</span>
                    <span className="unit not-uom "> x </span>
                    <span data-name="bypass_width_id" className="number_output" data-format="decimal">{hbStr(mp.casing_depth)}</span>
                  </OutRow>
                  <OutRow label={<span data-name="bypass_width_id">Bank W x H x D</span>}>
                    <span data-name="bypass_width_id">{hbStr(m.bank_wide)}</span>
                    <span className="unit not-uom "> x </span>
                    <span data-name="bypass_width_id">{hbStr(m.bank_high)}</span>
                    <span className="unit not-uom "> x </span>
                    <span data-name="bypass_width_id">{hbStr(m.bank_deep)}</span>
                  </OutRow>
                  <OutRow label={<span data-name="airway_width_id">Module Qty</span>}>
                    <span data-name="airway_width_id">{hbStr(m.module_qty)}</span>
                  </OutRow>
                  <OutRow label={<span data-name="airway_width_id">Airway Width</span>} uom={<span className="right uom">in</span>}>
                    <span data-name="airway_width_id" className="number_output" data-format="decimal">{hbStr(m.airway_width)}</span>
                  </OutRow>
                  <OutRow label={<span data-name="plate_spacing">Plate Spacing</span>} uom={<span className="right uom">in</span>}>
                    <span data-name="plate_spacing" className="number_output" data-format="decimal">{hbStr(mp.plate_spacing)}</span>
                  </OutRow>
                  <OutRow label={<span data-name="plate_thickness">Plate Thickness</span>} uom={<span className="right uom">in</span>}>
                    <span data-name="plate_thickness">{hbStr(mp.plate_thickness)}</span>
                  </OutRow>
                  <OutRow cls="row bottom-row row_with_negative_margin" label={<span data-name="weight_id">Weight</span>} uom={<span className="uom right">lb</span>}>
                    <span data-name="airway_width_id" className="number_output" data-format="integer">{hbStr(m.operating_weight)}</span>
                  </OutRow>
                </div>
              </div>

              {/* Output · Bypass (tmpl:1158-1235) */}
              <div className={`tab-pane fade${outputTab === 'ahu-Bypass_output' ? ' in active' : ''} scrollable-content col-xs-12 float-left full-height`} id="ahu-Bypass_output">
                <div className="row empty" />
                <div className="col-xs-12 col-md-12 col-lg-4 output-part-left">
                  <OutRow label={<span data-name="weight_id">Width</span>} uom={<span className="uom right">in</span>}>
                    <span data-name="airway_width_id" data-formatter_type="decimal">{hbStr(m.bypass_width)}</span>
                  </OutRow>
                  <OutRow label={<span data-name="weight_id">Damper Shaft Shape</span>}>
                    <span>{hbStr(m.dmr_shaft_shape_name)}</span>
                  </OutRow>
                  <OutRow label={<span data-name="weight_id">Damper Shaft Diameter</span>} uom={<span className="uom right">in</span>}>
                    <span>{hbStr(m.dmr_shaft_dia_name)}</span>
                  </OutRow>
                  <OutRow label={<span data-name="weight_id">Damper Shaft Length</span>} uom={<span className="uom right">in</span>}>
                    <span>{hbStr(m.dmr_shaft_length_name)}</span>
                  </OutRow>
                </div>
                <div className="col-xs-12 col-md-12 col-lg-6 col-xs-offset-2 output-part-right">
                  <OutRow label={<span data-name="bypass_width_id">Supply Mixed Air DB</span>} uom={<span className="uom temp right">F</span>}>
                    <span data-name="bypass_width_id" className="number_output" data-formatter_type="decimal">{hbStr(m.sma_db_temp_winter)}</span>
                  </OutRow>
                  <OutRow label={<span data-name="bypass_width_id">Airflow</span>} uom={<span className="uom right">CFM</span>}>
                    <span data-name="bypass_width_id" className="number_output" data-formatter_type="decimal">{hbStr(m.bypass_airflow)}</span>
                  </OutRow>
                  <OutRow label={<span data-name="bypass_width_id">Face Velocity</span>} uom={<span className="uom right">fpm</span>}>
                    <span data-name="bypass_width_id" className="number_output" data-formatter_type="integer">{hbStr(m.bypass_velocity)}</span>
                  </OutRow>
                  <OutRow label={<span data-name="bypass_width_id">Air PD</span>} uom={<span className="uom right">in WC</span>}>
                    <span data-name="bypass_width_id" data-formater_type="decimal">{hbStr(m.bypass_apd)}</span>
                  </OutRow>
                </div>
              </div>

              {/* Output · Performance (tmpl:1236-1732) */}
              <div className={`tab-pane fade${outputTab === 'ahu-Performance_output' ? ' in active' : ''}  scrollable-content col-xs-12 float-left full-height Performance`} id="ahu-Performance_output">
                <div className="sub-tab-menu nav-tabs">
                  <div className="row">
                    <ul className="col-xs-12 text-right output_performance_tabing summer-winter-tabing">
                      <li className={`winter winter-tab${season === 'winter' ? ' active' : ''}${winterDisabled ? ' non-clickable' : ''}`}>
                        <a tabIndex={-1} data-toggle="tab" data-tab="winter" onClick={(e) => { stop(e); setSeason('winter'); }}>
                          winter &#160;
                        </a>
                      </li>
                      <li className={`summer summer-tab${season === 'summer' ? ' active' : ''}${summerDisabled ? 'non-clickable' : ''}`}>
                        <a tabIndex={-1} data-toggle="tab" data-tab="summer" onClick={(e) => { stop(e); setSeason('summer'); }}>
                          summer
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="output_performance-tab-content">
                  <div id="output_winter" className={`winter tab-pane fade${season === 'winter' ? ' in active' : ''}`}>
                    <PhxWinterOutput m={m} />
                  </div>
                  {/* {{#ifCompare oa_airflow_actual_summer '>' 0}} (tmpl:1501) */}
                  {ifCompare(m.oa_airflow_actual_summer, '>', 0) && (
                    <div id="output_summer" className={`summer fade  tab-pane${season === 'summer' ? ' in active' : ''}`}>
                      <PhxSummerOutput m={m} />
                    </div>
                  )}
                </div>
              </div>

              {/* Output · Notes (tmpl:1734-1763) */}
              <div className={`tab-pane fade${outputTab === 'ahu-Notes-output' ? ' in active' : ''} scrollable-content col-xs-12 float-left full-height output-height`} id="ahu-Notes-output">
                <div className="row empty" />
                <div className="col-xs-12 col-md-12 col-lg-12 output-part-left">
                  <div className="row row_with_negative_margin">
                    {hbIf(m.notes) && (
                      <>
                        <div className="col-xs-2 label-part">
                          <div className="row row_with_negative_margin">
                            <Col12>
                              <span>Mfg&apos;s Notes :</span>
                            </Col12>
                          </div>
                        </div>
                        <div className="col-xs-10 output-part output_mfg_notes ">
                          {(Array.isArray(m.notes) ? (m.notes as Row[]) : []).map((n, ni) => (
                            <div className="row row_with_negative_margin" key={ni}>
                              <Col12>
                                <span>{hbStr(n.note)}</span>
                              </Col12>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="output-bottom output-part">
          <span data-name="airway_width_id">Rel.</span>
          <span data-name="airway_width_id">{hbStr(m.selection_api_dll_version)}</span>
        </div>
      </div>
    </div>
  );
};

/** Input Performance season block (tmpl:685-808 winter / 809-932 summer). */
const PhxSeasonInput = ({ m, season }: { m: Row; season: 'winter' | 'summer' }) => {
  const s = season;
  return (
    <>
      <div className="col-xs-12 col-md-12 col-lg-4 input-part-left">
        <div className="row">
          <div className={`col-xs-6 input-part${s === 'winter' ? ' col-xs-offset-6' : ''}`}>
            <div className="row">
              <Col12>
                <span>SUPPLY AIR ENTERING</span>
              </Col12>
            </div>
          </div>
        </div>
        <InRow label={<span data-name="ra_air_stream_winter_id">Air Stream</span>}>
          <Col12>
            <ROInput value={m[`oa_air_stream_${s}`]} />
          </Col12>
        </InRow>
        <InRow
          label={
            <>
              <span data-name="ra_air_stream_winter_id">Airflow</span>
              <span className="right uom">CFM</span>
            </>
          }
        >
          <Col12>
            <ROInput value={m[`oa_airflow_actual_${s}`]} cls="number_output" fmt="integer" />
          </Col12>
        </InRow>
        <InRow
          label={
            <>
              <span>DB Temperature</span>
              <span className="uom temp right">F</span>
            </>
          }
        >
          <Col12>
            <ROInput value={m[`oa_db_temp_${s}`]} cls="number_output" fmt="decimal" />
          </Col12>
        </InRow>
        <InRow
          label={
            <>
              <span>Humidity</span>
              <span className="uom right">%</span>
            </>
          }
        >
          <Col12>
            <ROInput value={m[`oa_rh_${s}`]} />
          </Col12>
        </InRow>
      </div>
      <div className="col-xs-12 col-md-12 col-lg-3 input-part-right input_part_right no-left-padding">
        <div className="row">
          <div className="col-xs-2 input-part" />
          <div className="col-xs-8 input-part fix-input-part-width">
            <div className="row">
              <Col12>
                <span>EXHAUST AIR ENTERING</span>
              </Col12>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-xs-2 input-part" />
          <div className="col-xs-8 input-part fix-input-part-width">
            <div className="row">
              <Col12>
                <ROInput value={m[`ra_air_stream_${s}`]} />
              </Col12>
            </div>
          </div>
        </div>
        {(
          [
            [<span className="uom right" key="u">CFM</span>, m[`ra_airflow_actual_${s}`], 'integer'],
            [<span className="uom right temp" key="u">F</span>, m[`ra_db_temp_${s}`], 'decimal'],
            [<span className="uom right" key="u">%</span>, m[`ra_rh_${s}`], undefined],
          ] as Array<[ReactNode, unknown, 'integer' | 'decimal' | undefined]>
        ).map(([uom, value, fmt], ri) => (
          <div className="row" key={ri}>
            <div className="col-xs-2 label-part">{uom}</div>
            <div className="col-xs-8 input-part fix-input-part-width">
              <div className="row">
                <Col12>
                  {fmt ? <ROInput value={value} cls="number_output" fmt={fmt} /> : <ROInput value={value} />}
                </Col12>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

/** A supply-leaving output row (left column of the output Performance tab). */
const SupplyRow = ({
  label,
  uom,
  children,
}: {
  label: string;
  uom?: ReactNode;
  children: ReactNode;
}) => (
  <div className="row row_with_negative_margin">
    <div className="col-xs-6 label-part">
      <span data-name="bypass_width_id">{label}</span>
      {uom}
    </div>
    <div className="col-xs-6 output-part">{children}</div>
  </div>
);

/** An exhaust-leaving output row (middle column). */
const ExhaustRow = ({
  uom,
  value,
  fmt,
  noLeftPadding,
}: {
  uom?: ReactNode;
  value: unknown;
  fmt?: 'integer' | 'decimal';
  noLeftPadding?: boolean;
}) => (
  <div className="row row_with_negative_margin">
    <div className={`col-xs-2 label-part${noLeftPadding ? ' no-left-padding' : ''}`}>{uom}</div>
    <div className="col-xs-7 output-part">
      <span data-name="bypass_width_id" data-formatter_type={fmt}>
        {hbStr(value)}
      </span>
    </div>
  </div>
);

/** A "General" column row (right column). */
const GeneralRow = ({ label, uom, children }: { label: string; uom?: ReactNode; children: ReactNode }) => (
  <div className="row">
    <div className="col-xs-8 label-part">
      <span data-name="bypass_width_id">{label}</span>
      {uom}
    </div>
    <div className="col-xs-4 output-part">
      <div className="row">
        <div className="col-xs-12 output-part-left no-left-padding">{children}</div>
      </div>
    </div>
  </div>
);

/** Winter output block (tmpl:1250-1500). */
const PhxWinterOutput = ({ m }: { m: Row }) => (
  <>
    <div className="col-xs-12 col-md-12 col-lg-4 output-part-left">
      <div className="row row_with_negative_margin">
        <div className="col-xs-6 label-part col-xs-offset-6">
          <span data-name="bypass_width_id">SUPPLY AIR LEAVING</span>
        </div>
      </div>
      <SupplyRow label="Air Stream">
        <span data-name="bypass_width_id">{hbStr(m.oa_air_stream_winter)}</span>
      </SupplyRow>
      <SupplyRow label="Airflow" uom={<span className="uom right">CFM</span>}>
        <span data-name="bypass_width_id" className="number_output" data-formatter_type="integer">{hbStr(m.oa_airflow_actual_winter)}</span>
      </SupplyRow>
      <SupplyRow label="DB Temperature" uom={<span className="uom right temp">F</span>}>
        <span data-name="bypass_width_id" data-formatter_type="decimal">{hbStr(m.sa_db_temp_winter)}</span>
      </SupplyRow>
      <SupplyRow label="Humidity" uom={<span className="uom right">%</span>}>
        <span data-name="bypass_width_id" className="number_output" data-formatter_type="decimal">{hbStr(m.sa_rh_winter)}</span>
      </SupplyRow>
      <SupplyRow label="Face Velocity" uom={<span className="uom right">fpm</span>}>
        <span data-name="bypass_width_id" data-formatter_type="integer">{hbStr(m.sa_face_velocity_winter)}</span>
      </SupplyRow>
      <SupplyRow label="Air PD / Max" uom={<span className="uom right">in WC</span>}>
        <span data-name="bypass_width_id" data-formatter_type="decimal">{hbStr(m.sa_apd_winter)}</span>
        {hbIf(m.max_permissible_dp) && (
          <>
            {' / '}
            <span data-name="bypass_width_id" data-formatter_type="decimal">{hbStr(m.max_permissible_dp)}</span>
          </>
        )}
      </SupplyRow>
      <SupplyRow label="Frosted Surface" uom={<span className="uom right">%</span>}>
        <span data-name="bypass_width_id" data-formatter_type="decimal">{hbStr(m.sa_frost_surface_percent_winter)}</span>
      </SupplyRow>
    </div>
    <div className="col-xs-12 col-md-12 col-lg-3 output-part-left no-left-padding">
      <div className="row row_with_negative_margin">
        <div className="col-xs-9 sub-titles label-part no-right-padding col-xs-offset-2">
          <span className="text-uppercase sub-titles__text">EXHAUST AIR LEAVING</span>
        </div>
      </div>
      {/* NOTE: legacy renders oa_air_stream_winter for the exhaust stream
          (tmpl:1333-1335). */}
      <ExhaustRow value={m.oa_air_stream_winter} />
      <ExhaustRow uom={<span className="uom right">CFM</span>} value={m.ra_airflow_actual_winter} fmt="integer" />
      <ExhaustRow uom={<span className="uom right temp">F</span>} value={m.ea_db_temp_winter} fmt="decimal" />
      <ExhaustRow uom={<span className="uom right ">%</span>} value={m.ea_rh_winter} />
      <ExhaustRow uom={<span className="uom right ">fpm</span>} value={m.ea_face_velocity_winter} fmt="integer" />
      <ExhaustRow uom={<span className="uom right ">in WC</span>} value={m.ea_apd_winter} fmt="decimal" noLeftPadding />
      <ExhaustRow uom={<span className="uom right ">%</span>} value={m.ea_frost_surface_percent_winter} fmt="decimal" />
    </div>
    <div className="col-xs-12 col-md-12 col-lg-5 output-part-right">
      <div className="row">
        <div className="col-xs-12 sub-titles no-right-padding  label-part">
          <span className="text-uppercase sub-titles__text ">General</span>
        </div>
      </div>
      <GeneralRow label="Temperature Effectiveness Dry / Wet" uom={<span className="uom right">%</span>}>
        <span className="not-empty" data-name="bypass_width_id" data-formatter_type="decimal">{hbStr(m.dry_temp_effectiveness_winter)}</span>
        {hbIf(m.wet_temp_effectiveness_winter) && (
          <span className="not-empty" data-name="bypass_width_id" data-formatter_type="decimal">{hbStr(m.wet_temp_effectiveness_winter)}</span>
        )}
      </GeneralRow>
      <GeneralRow label="Temperature Ratio Dry / Wet" uom={<span className="uom right">%</span>}>
        <span data-name="bypass_width_id" data-formatter_type="decimal" className="not-empty">{hbStr(m.dry_temp_ratio_winter)}</span>
        {hbIf(m.wet_temp_ratio_winter) && (
          <>
            {' / '}
            <span data-name="bypass_width_id" data-formatter_type="decimal" className="right-span not-empty">{hbStr(m.wet_temp_ratio_winter)}</span>
          </>
        )}
      </GeneralRow>
      <GeneralRow label="Frost Starting Temperature" uom={<span className="uom temp right">F</span>}>
        <span data-name="bypass_width_id" data-formatter_type="decimal">{hbStr(m.frost_starting_temp_winter)}</span>
      </GeneralRow>
      <GeneralRow label="Heat Recovery Class">
        <span data-name="bypass_width_id">{hbStr(m.hr_class)}</span>
      </GeneralRow>
      <GeneralRow label="Energy Efficiency EN13053 / ASHRAE" uom={<span className="uom right">%</span>}>
        <span data-name="bypass_width_id" data-formatter_type="decimal">{hbStr(m.energy_efficiency_en13053)}</span>
        {hbIf(m.efficiency_winter_ashrae) && (
          <>
            {' / '}
            <span data-name="bypass_width_id" data-formatter_type="decimal">{hbStr(m.efficiency_winter_ashrae)} </span>
          </>
        )}
      </GeneralRow>
      <GeneralRow label="HRS Capacity" uom={<span className="uom right">btu/hr</span>}>
        <span data-name="bypass_width_id" data-formatter_type="integer">{hbStr(m.hrs_capacity_winter)}</span>
      </GeneralRow>
      <GeneralRow label="ERP 1253/2014" uom={<span className="uom right">%</span>}>
        <span data-name="bypass_width_id" data-formatter_type="decimal">{hbStr(m.erp_1253_2014_winter)}</span>
      </GeneralRow>
    </div>
  </>
);

/** Summer output block (tmpl:1502-1730). */
const PhxSummerOutput = ({ m }: { m: Row }) => (
  <>
    <div className="col-xs-12 col-md-12 col-lg-4 output-part-left">
      <div className="row row_with_negative_margin">
        <div className="col-xs-6 label-part col-xs-offset-6">
          <span data-name="bypass_width_id">SUPPLY AIR LEAVING</span>
        </div>
      </div>
      <SupplyRow label="Air Stream">
        <span data-name="bypass_width_id">{hbStr(m.oa_air_stream_summer)}</span>
      </SupplyRow>
      <SupplyRow label="Airflow" uom={<span className="uom right">CFM</span>}>
        <span data-name="bypass_width_id" className="number_output" data-formatter_type="integer">{hbStr(m.sa_airflow_actual_summer)}</span>
      </SupplyRow>
      <SupplyRow label="DB Temperature" uom={<span className="uom right temp">F</span>}>
        <span data-name="bypass_width_id" data-formatter_type="decimal">{hbStr(m.sa_db_temp_summer)}</span>
      </SupplyRow>
      <SupplyRow label="Humidity" uom={<span className="uom right">%</span>}>
        <span data-name="bypass_width_id" className="number_output" data-formatter_type="decimal">{hbStr(m.sa_rh_summer)}</span>
      </SupplyRow>
      <SupplyRow label="Face Velocity" uom={<span className="uom right">fpm</span>}>
        <span data-name="bypass_width_id" data-formatter_type="integer">{hbStr(m.sa_face_velocity_summer)}</span>
      </SupplyRow>
      <SupplyRow label="Air PD / Max" uom={<span className="uom right">in WC</span>}>
        <span data-name="bypass_width_id" data-formatter_type="decimal">{hbStr(m.sa_apd_summer)}</span>
        {hbIf(m.max_permissible_dp) && (
          <>
            {' / '}
            <span data-name="bypass_width_id" data-formatter_type="decimal">{hbStr(m.max_permissible_dp)}</span>
          </>
        )}
      </SupplyRow>
      <SupplyRow label="Condensation" uom={<span className="uom right">gph</span>}>
        <span data-name="bypass_width_id" data-formatter_type="decimal">{hbStr(m.sa_condensation_rate_summer)}</span>
      </SupplyRow>
    </div>
    <div className="col-xs-12 col-md-12 col-lg-3 output-part-left no-left-padding">
      <div className="row row_with_negative_margin">
        <div className="col-xs-9 sub-titles label-part no-right-padding col-xs-offset-2">
          <span className="text-uppercase sub-titles__text">EXHAUST AIR LEAVING</span>
        </div>
      </div>
      <ExhaustRow value={m.ea_air_stream_summer} />
      <ExhaustRow uom={<span className="uom right">CFM</span>} value={m.ea_airflow_actual_summer} fmt="integer" />
      <ExhaustRow uom={<span className="uom right temp">F</span>} value={m.ea_db_temp_summer} fmt="decimal" />
      <ExhaustRow uom={<span className="uom right ">%</span>} value={m.ea_rh_summer} />
      <ExhaustRow uom={<span className="uom right ">fpm</span>} value={m.ea_face_velocity_summer} fmt="integer" />
      <ExhaustRow uom={<span className="uom right ">in WC</span>} value={m.ea_apd_summer} fmt="decimal" noLeftPadding />
    </div>
    <div className="col-xs-12 col-md-12 col-lg-5 output-part-right">
      <div className="row">
        <div className="col-xs-12 sub-titles no-right-padding  label-part">
          <span className="text-uppercase sub-titles__text ">General</span>
        </div>
      </div>
      <GeneralRow label="Temperature Effectiveness Dry / Wet" uom={<span className="uom right">%</span>}>
        <span className="not-empty" data-name="bypass_width_id" data-formatter_type="decimal">{hbStr(m.dry_temp_effectiveness_summer)}</span>
        {hbIf(m.wet_temp_effectiveness_summer) && (
          <span className="not-empty" data-name="bypass_width_id" data-formatter_type="decimal">{hbStr(m.wet_temp_effectiveness_summer)}</span>
        )}
      </GeneralRow>
      <GeneralRow label="Temperature Ratio Dry / Wet" uom={<span className="uom right">%</span>}>
        <span data-name="bypass_width_id" data-formatter_type="decimal" className="not-empty">{hbStr(m.dry_temp_ratio_summer)}</span>
        {hbIf(m.wet_temp_ratio_summer) && (
          <>
            {' / '}
            <span data-name="bypass_width_id" data-formatter_type="decimal" className="right-span not-empty">{hbStr(m.wet_temp_ratio_summer)}</span>
          </>
        )}
      </GeneralRow>
      <GeneralRow label="Heat Recovery Class">
        <span data-name="bypass_width_id">{hbStr(m.hr_class)}</span>
      </GeneralRow>
      <GeneralRow label="Energy Efficiency EN13053 / ASHRAE" uom={<span className="uom right">%</span>}>
        <span data-name="bypass_width_id" data-formatter_type="decimal">{hbStr(m.energy_efficiency_en13053)}</span>
        {hbIf(m.efficiency_summer_ashrae) && (
          <>
            {' / '}
            <span data-name="bypass_width_id" data-formatter_type="decimal">{hbStr(m.efficiency_summer_ashrae)} </span>
          </>
        )}
      </GeneralRow>
      <GeneralRow label="HRS Capacity" uom={<span className="uom right">btu/hr</span>}>
        <span data-name="bypass_width_id" data-formatter_type="integer">{hbStr(m.hrs_capacity_summer)}</span>
      </GeneralRow>
      <GeneralRow label="ERP 1253/2014" uom={<span className="uom right">%</span>}>
        <span data-name="bypass_width_id" data-formatter_type="decimal">{hbStr(m.erp_1253_2014_summer)}</span>
      </GeneralRow>
    </div>
  </>
);

export default PhxSelectionTab;
